import type React from "react";
import { useState } from "react";
import { Button } from "../Button";
import { Card } from "../Card";
import { Switch } from "../Switch";
import { Text } from "../Text";
import styles from "./CookieBanner.module.scss";

export interface CookieConsent {
  analytics: boolean;
  marketing: boolean;
}

export interface CookieBannerProps {
  onAccept: (consent: CookieConsent) => void;
  onDecline: () => void;
  isOpen: boolean;
  title?: string;
  description?: string;
  acceptAllLabel?: string;
  declineLabel?: string;
  manageLabel?: string;
  saveLabel?: string;
  backLabel?: string;
  necessaryLabel?: string;
  analyticsLabel?: string;
  marketingLabel?: string;
  necessaryDescription?: string;
  analyticsDescription?: string;
  marketingDescription?: string;
}

export const CookieBanner: React.FC<CookieBannerProps> = ({
  onAccept,
  onDecline,
  isOpen,
  title = "Respecting your privacy",
  description = "We use cookies to improve your experience and analyze how our 3D guidebook is used. You can manage your preferences or accept all cookies.",
  acceptAllLabel = "Accept All",
  declineLabel = "Necessary Only",
  manageLabel = "Manage Preferences",
  saveLabel = "Save Preferences",
  backLabel = "Back",
  necessaryLabel = "Strictly Necessary",
  analyticsLabel = "Analytics",
  marketingLabel = "Marketing & Personalization",
  necessaryDescription = "Essential for the website to function properly.",
  analyticsDescription = "Helps us understand how users interact with our site.",
  marketingDescription = "Used to deliver relevant content and advertisements.",
}) => {
  const [showPreferences, setShowPreferences] = useState(false);
  const [consent, setConsent] = useState<CookieConsent>({
    analytics: true,
    marketing: false,
  });

  if (!isOpen) return null;

  const handleSavePreferences = () => {
    onAccept(consent);
  };

  const handleAcceptAll = () => {
    onAccept({ analytics: true, marketing: true });
  };

  if (showPreferences) {
    return (
      <div className={styles.root}>
        <Card variant="elevated" className={styles.bannerCard} padded>
          <div className={styles.header}>
            <Text variant="medium" weight="bold">
              {manageLabel}
            </Text>
          </div>
          <div className={styles.preferencesList}>
            <div className={styles.preferenceItem}>
              <div className={styles.preferenceInfo}>
                <Text variant="small" weight="bold">
                  {necessaryLabel}
                </Text>
                <Text variant="xsmall" color="secondary">
                  {necessaryDescription}
                </Text>
              </div>
              <Switch checked disabled onChange={() => {}} />
            </div>

            <div className={styles.preferenceItem}>
              <div className={styles.preferenceInfo}>
                <Text variant="small" weight="bold">
                  {analyticsLabel}
                </Text>
                <Text variant="xsmall" color="secondary">
                  {analyticsDescription}
                </Text>
              </div>
              <Switch
                checked={consent.analytics}
                onChange={(checked) =>
                  setConsent((prev) => ({ ...prev, analytics: checked }))
                }
              />
            </div>

            <div className={styles.preferenceItem}>
              <div className={styles.preferenceInfo}>
                <Text variant="small" weight="bold">
                  {marketingLabel}
                </Text>
                <Text variant="xsmall" color="secondary">
                  {marketingDescription}
                </Text>
              </div>
              <Switch
                checked={consent.marketing}
                onChange={(checked) =>
                  setConsent((prev) => ({ ...prev, marketing: checked }))
                }
              />
            </div>
          </div>
          <div className={styles.actions}>
            <Button
              variant="ghost"
              size="small"
              onClick={() => setShowPreferences(false)}
            >
              {backLabel}
            </Button>
            <Button
              variant="primary"
              size="small"
              onClick={handleSavePreferences}
            >
              {saveLabel}
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className={styles.root}>
      <Card variant="elevated" className={styles.bannerCard} padded>
        <div className={styles.content}>
          <Text variant="medium" weight="bold">
            {title}
          </Text>
          <Text variant="small" color="secondary">
            {description}
          </Text>
        </div>
        <div className={styles.actions}>
          <Button
            variant="ghost"
            size="small"
            onClick={() => setShowPreferences(true)}
          >
            {manageLabel}
          </Button>
          <Button variant="ghost" size="small" onClick={onDecline}>
            {declineLabel}
          </Button>
          <Button variant="primary" size="small" onClick={handleAcceptAll}>
            {acceptAllLabel}
          </Button>
        </div>
      </Card>
    </div>
  );
};
